import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import AgendaTimeSlot from "@/domain/entities/agenda/AgendaTimeSlot";
import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import Email, { MailsListId } from "@/domain/entities/emails/Email";
import AIResponse, {
    ComponentDataContentType,
    MoreDataCallbackResult,
} from "@/domain/entities/vistassistant/AIResponse";
import { TicketType } from "@/domain/entities/vistassistant/TicketTypes";
import type {
    CreateUserVoiceRequestData,
    PendingTicketFormData,
    ReportBugData,
    SearchSimilarTicketsResult,
} from "@/domain/entities/vistassistant/TicketTypes";
import UserRequest from "@/domain/entities/vistassistant/UserRequest";
import { answerMailsListId } from "@/hooks/mails/assistantMailsCache";
import { queryKeys, STALE_TIME } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import type { AnswerFeedback } from "@cs_teamwaydev/mailvista-sdk_react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
    CircleAlert,
    ListChecks,
    Loader2,
    RotateCcw,
    ThumbsDown,
    ThumbsUp,
} from "lucide-react";
import React, { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import ActionConfirmation from "./ActionConfirmation";
import AgendaTimeSlotsResponse from "./AgendaTimeSlotsResponse";
import AssistantAnswer from "./AssistantAnswer";
import { pillButtonClassName } from "./assistantClasses";
import ChatEventList from "./ChatEventList";
import ChatMailList from "./ChatMailList";
import ConversationBubble from "./ConversationBubble";
import CreateAgendaItemResponse from "./CreateAgendaItemResponse";
import ModelMarkdown from "./ModelMarkdown";
import ReasoningBlock from "./ReasoningBlock";
import ReportBugResponse from "./ReportBugResponse";
import SearchSimilarTicketsResponse from "./SearchSimilarTicketsResponse";
import { PulsingDots } from "./ThinkingIndicator";
import UserVoiceResponse from "./UserVoiceResponse";
import { resolveAssistantTextBubbles } from "./vistassistantMessageLayers";

const FEEDBACK_ICONS = { up: ThumbsUp, down: ThumbsDown } as const;

const AnswerFeedbackButtons = ({
    value,
    onChange,
}: {
    value?: AnswerFeedback;
    onChange: (feedback: AnswerFeedback) => void;
}) => {
    const { t } = useTranslation();
    const questionId = useId();

    return (
        <div
            aria-labelledby={questionId}
            className="flex flex-wrap items-center gap-1"
            role="group"
        >
            <span className="mr-2 text-xs text-assistant-muted" id={questionId}>
                {t("vistassistant.feedback.question")}
            </span>
            {(["up", "down"] as const).map((feedback) => {
                const Icon = FEEDBACK_ICONS[feedback];
                const chosen = value === feedback;

                return (
                    <button
                        key={feedback}
                        aria-label={t(`vistassistant.feedback.${feedback}`)}
                        aria-pressed={chosen}
                        className={cn(
                            "flex size-9 items-center justify-center rounded-full transition-colors hover:bg-assistant-chip",
                            chosen
                                ? "text-primary-dark"
                                : "text-primary-darkest",
                        )}
                        onClick={() => onChange(feedback)}
                        title={t(`vistassistant.feedback.${feedback}`)}
                        type="button"
                    >
                        <Icon
                            aria-hidden
                            className={cn("size-5", chosen && "fill-current")}
                            strokeWidth={1.5}
                        />
                    </button>
                );
            })}
        </div>
    );
};

/** The assistant's own text: no bubble, line by line. */
const AssistantText = ({ text }: { text: string }) => (
    <div className="flex animate-opacity flex-col whitespace-pre-wrap text-base leading-6 text-primary-darkest">
        {text.split("\n").map((line) => (
            <p key={line}>{line}</p>
        ))}
    </div>
);

/**
 * The analyses of a whole answer (todo, synthesis, any question on the mails' text), after everything
 * it shows: they arrive later and sum up what is above (v1 contract; with the step-based contract,
 * the assistant writes the answer itself).
 */
const TurnAnalyses = ({ responses }: { responses: AIResponse[] }) => {
    const { t } = useTranslation();

    return (
        <>
            {responses.map((response) => (
                <React.Fragment key={response.userRequestId}>
                    {response.isAnalysisPending && (
                        <p className="flex items-center gap-2 text-[13px] text-neutral-dark">
                            <Loader2
                                aria-hidden
                                className="h-3.5 w-3.5 animate-spin"
                            />
                            {t("vistassistant.deepAnalysis.pending")}
                        </p>
                    )}
                    {response.deepAnalysis.result && (
                        <ConversationBubble className="flex flex-col gap-2 whitespace-normal">
                            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-primary-darker">
                                <ListChecks
                                    aria-hidden
                                    className="h-3.5 w-3.5"
                                />
                                {t("vistassistant.deepAnalysis.title")}
                            </span>
                            <div className="markdown-content text-sm">
                                <ModelMarkdown>
                                    {response.deepAnalysis.result.result}
                                </ModelMarkdown>
                            </div>
                        </ConversationBubble>
                    )}
                    {response.deepAnalysis.error && (
                        <p className="flex items-center gap-2 text-sm text-error-darkest">
                            <CircleAlert
                                aria-hidden
                                className="h-4 w-4 flex-none"
                            />
                            {t("vistassistant.deepAnalysis.error")}
                        </p>
                    )}
                </React.Fragment>
            ))}
        </>
    );
};

interface MessageItemProps {
    message: UserRequest | AIResponse;
    handleMoreDataCallBack: (
        callback: () => Promise<MoreDataCallbackResult>,
    ) => void;
    isLoadingMoreData: boolean;
    isLastMessage: boolean;
    chosenReply?: string;
    /**
     * Last block of an answer: the analyses of all its blocks, shown after them. Absent (other
     * blocks), the block shows no analysis.
     */
    turnAnalyses?: AIResponse[];
    onAction?: () => void | Promise<void>;
    actionDone?: boolean;
    /** "Retry" on the error bubble of a failed turn (F-1.17); only given to the last message. */
    onRetry?: () => void;
    /** Step-based contract: the thumb given to this answer, if any. */
    feedback?: AnswerFeedback;
    /** Step-based contract: thumbs under an answered turn (absent: no thumbs). */
    onFeedback?: (turnId: string, feedback: AnswerFeedback) => void;
    onSubmitTicket?: (
        data: PendingTicketFormData,
        ticketType: TicketType,
    ) => Promise<void>;
    onConfirmCreateFromSearch?: (
        pendingData: PendingTicketFormData,
        ticketType: TicketType,
    ) => void;
}

const MessageItem = ({
    message,
    handleMoreDataCallBack,
    isLoadingMoreData,
    isLastMessage,
    chosenReply,
    turnAnalyses,
    onAction,
    actionDone,
    onRetry,
    feedback,
    onFeedback,
    onSubmitTicket,
    onConfirmCreateFromSearch,
}: MessageItemProps) => {
    const { t } = useTranslation();
    const isUserRequest = message instanceof UserRequest;
    const isIAResponse = message instanceof AIResponse;

    const getEmailsFromMessage = () => {
        if (!isIAResponse)
            return {
                data: [],
            };

        if (
            !message.content?.contentData ||
            !Array.isArray(message.content.contentData)
        ) {
            return {
                data: [],
            };
        }

        return {
            data: message.content.contentData.filter(
                (item: any) => item instanceof Email,
            ),
        };
    };

    // Only a message that carries mails reads them through the list cache: a question or a text answer
    // has none, and its query used to run anyway, then re-render the bubble (audit 15/10, P4-07).
    const carriesEmails =
        isIAResponse &&
        Array.isArray(message.content?.contentData) &&
        message.content.contentData.some(
            (item: unknown) => item instanceof Email,
        );

    const { data: emailsFromMessage } = useInfiniteQuery({
        // Same infinite query format as for other mails list for action management compatibility
        queryKey: queryKeys.mails.list((message as AIResponse).userRequestId),
        queryFn: getEmailsFromMessage,
        getNextPageParam: () => null, // unused but required for infinite query
        initialPageParam: 0, // unused but required for infinite query
        staleTime: STALE_TIME.session,
        enabled: carriesEmails,
    });

    const emails = useMemo(
        () => emailsFromMessage?.pages.flatMap((page) => page.data),
        [emailsFromMessage],
    );

    // Mails of the answer as received; as displayed ("load more" included), or as received until
    // that list is loaded.
    const receivedCount =
        isIAResponse && Array.isArray(message.content.contentData)
            ? message.content.contentData.length
            : 0;
    const displayedCount = emails?.length ?? receivedCount;

    // Failed turn (F-1.17): an error bubble with its cause replaces the SDK text.
    const errorCause = isIAResponse ? message.errorCause : undefined;

    const iaResponse = isIAResponse ? message : null;
    // Step-based contract: the answer replaces the SDK text; its proposal (content) follows it.
    const answer = !errorCause ? iaResponse?.answer : undefined;
    // Neither an error nor the step-based answer: the SDK's texts show.
    const showsSdkText = !errorCause && !answer;
    const turnId = iaResponse?.turnId;
    const canGiveFeedback = !!turnId && !!onFeedback;
    const { commentText, contentText, showEmptyReply, emptyReplyText } =
        resolveAssistantTextBubbles({
            aiComment: iaResponse?.aiComment,
            aiVocalText: iaResponse?.aiVocalText,
            contentType: iaResponse?.content.contentType ?? "",
            contentData: iaResponse?.content.contentData,
        });
    const emptyReplyFallback = showEmptyReply
        ? emptyReplyText || t("vistassistant.noResponse")
        : "";

    if (isUserRequest) {
        return <ConversationBubble isUserRequest message={message.content} />;
    }

    return (
        <div className="flex w-full min-w-0 flex-col gap-3">
            {!errorCause && !!iaResponse?.reasoning?.length && (
                <ReasoningBlock items={iaResponse.reasoning} />
            )}
            {answer && (
                <AssistantAnswer
                    answer={answer}
                    chosenReply={chosenReply}
                    isPast={!isLastMessage}
                    listId={answerMailsListId(message.userRequestId)}
                />
            )}
            {!!commentText && showsSdkText && (
                <AssistantText text={commentText} />
            )}
            {errorCause && (
                <ConversationBubble className="flex flex-wrap items-center gap-x-2.5 gap-y-2 whitespace-normal">
                    <CircleAlert
                        aria-hidden
                        className="h-4 w-4 flex-none text-error-dark"
                    />
                    <p className="min-w-0 flex-1">
                        {t(`vistassistant.turnError.${errorCause}`)}
                    </p>
                    {errorCause !== "quota" && onRetry && (
                        <Button
                            className={cn(pillButtonClassName, "h-9 text-sm")}
                            onClick={onRetry}
                            type="button"
                            variant="outline"
                        >
                            <RotateCcw aria-hidden />
                            {t("vistassistant.turnError.retry")}
                        </Button>
                    )}
                </ConversationBubble>
            )}
            {isIAResponse && (
                <>
                    {message.content.contentType ===
                        ComponentDataContentType.Mails &&
                        emails &&
                        emails.length > 0 && (
                            <ChatMailList
                                listId={message.userRequestId as MailsListId}
                                mails={emails}
                            />
                        )}
                    {!!contentText && showsSdkText && (
                        <AssistantText text={contentText} />
                    )}
                    {!!emptyReplyFallback && showsSdkText && (
                        <AssistantText text={emptyReplyFallback} />
                    )}
                    {message.content.contentType ===
                        ComponentDataContentType.AgendaItems &&
                        Array.isArray(message.content.contentData) &&
                        message.content.contentData.length > 0 && (
                            <ChatEventList
                                events={
                                    message.content.contentData as AgendaItem[]
                                }
                            />
                        )}
                    {message.content.contentType ===
                        ComponentDataContentType.CreateAgendaItem && (
                        <CreateAgendaItemResponse
                            eventData={
                                message.content
                                    .contentData as CreateAgendaItemData
                            }
                        />
                    )}
                    {message.content.contentType ===
                        ComponentDataContentType.FreeTimeSlots && (
                        <AgendaTimeSlotsResponse
                            timeSlots={
                                message.content.contentData as AgendaTimeSlot[]
                            }
                        />
                    )}
                    {message.content.contentType ===
                        ComponentDataContentType.ReportBug &&
                        onSubmitTicket && (
                            <ReportBugResponse
                                data={
                                    message.content.contentData as ReportBugData
                                }
                                onSubmit={async (data) =>
                                    onSubmitTicket(data, TicketType.Bug)
                                }
                            />
                        )}
                    {message.content.contentType ===
                        ComponentDataContentType.CreateUserVoiceRequest &&
                        onSubmitTicket && (
                            <UserVoiceResponse
                                data={
                                    message.content
                                        .contentData as CreateUserVoiceRequestData
                                }
                                onSubmit={async (data) =>
                                    onSubmitTicket(data, TicketType.UserVoice)
                                }
                            />
                        )}
                    {message.content.contentType ===
                        ComponentDataContentType.SearchSimilarTickets &&
                        onConfirmCreateFromSearch && (
                            <SearchSimilarTicketsResponse
                                data={
                                    message.content
                                        .contentData as SearchSimilarTicketsResult
                                }
                                onConfirmCreate={onConfirmCreateFromSearch}
                            />
                        )}
                </>
            )}
            {isIAResponse && message.content.moreDataCallBack && (
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-neutral-dark">
                    {isLoadingMoreData ? (
                        <PulsingDots />
                    ) : (
                        <>
                            <p>
                                {t(
                                    message.content.contentType ===
                                        ComponentDataContentType.AgendaItems
                                        ? "vistassistant.moreDataCallback.agendaText"
                                        : "vistassistant.moreDataCallback.text",
                                )}
                            </p>
                            <Button
                                className={pillButtonClassName}
                                disabled={isLoadingMoreData}
                                onClick={() => {
                                    handleMoreDataCallBack(
                                        message.content.moreDataCallBack!,
                                    );
                                }}
                                type="button"
                                variant="outline"
                            >
                                {t(
                                    message.content.contentType ===
                                        ComponentDataContentType.AgendaItems
                                        ? "vistassistant.moreDataCallback.agendaButton"
                                        : "vistassistant.moreDataCallback.button",
                                )}
                            </Button>
                        </>
                    )}
                </div>
            )}
            {isIAResponse && (
                <ActionConfirmation
                    actionDone={actionDone}
                    contentAction={message.content.contentAction}
                    displayedCount={displayedCount}
                    isLastMessage={isLastMessage}
                    onAction={onAction}
                    receivedCount={receivedCount}
                />
            )}
            {!!turnAnalyses?.length && (
                <TurnAnalyses responses={turnAnalyses} />
            )}
            {!errorCause && canGiveFeedback && (
                <AnswerFeedbackButtons
                    onChange={(value) => onFeedback(turnId, value)}
                    value={feedback}
                />
            )}
        </div>
    );
};

export default React.memo(MessageItem);
